import { Button, Center, Checkbox, Group, NumberInput, Radio, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import type { UseFieldReturnType } from '@mantine/form';
import { useIsFirstRender } from '@mantine/hooks';
import {
  IconArrowRight,
  IconCalendarRepeat,
  IconCalendarWeek,
  IconHash,
  IconSunrise,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { isNullish } from 'remeda';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import type { DelayType, InstallmentType } from '../PaymentPlanWizard';

type Step3Props = {
  delayDayInMonth: UseFieldReturnType<Date>;
  delayInDays: UseFieldReturnType<number | string>;
  delayType: UseFieldReturnType<DelayType>;
  firstDeadlineDate: UseFieldReturnType<Date>;
  installmentType: UseFieldReturnType<InstallmentType>;
  installmentValue: UseFieldReturnType<number | string>;
  lateFees: UseFieldReturnType<boolean, 'checkbox'>;
  onClickNext: VoidFunction;
  onVisit: VoidFunction;
};

export const Step3 = ({
  delayDayInMonth,
  delayInDays,
  delayType,
  firstDeadlineDate,
  installmentType,
  installmentValue,
  lateFees,
  onClickNext,
  onVisit,
}: Step3Props) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const isFirstRender = useIsFirstRender();

  useEffect(() => {
    if (isFirstRender) return;
    if (installmentType.getValue() === 'quantity') installmentValue.setValue(12);
    else installmentValue.setValue(0);
  }, [installmentType.getValue()]);

  useEffect(() => onVisit(), []);

  const canClickNext = (() => {
    if (typeof installmentValue.getValue() === 'string') return false;
    if (typeof delayInDays.getValue() === 'string') return false;

    return true;
  })();

  return (
    <Center>
      <Stack miw="450px" maw="50%" w="100%">
        <Radio.Group
          mt="xl"
          value={installmentType.getValue()}
          onChange={val => installmentType.setValue(val as InstallmentType)}
        >
          <Group>
            <Radio
              w="50%"
              label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.NUMBER_OF_INSTALLMENTS)}
              value="quantity"
            />
            <Radio
              label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.AMOUNT_PER_INSTALLMENT)}
              value="amount"
            />
          </Group>
        </Radio.Group>
        {installmentType.getValue() === 'quantity' ? (
          <NumberInput
            {...installmentValue.getInputProps()}
            allowDecimal={false}
            allowLeadingZeros={false}
            allowNegative={false}
            decimalSeparator=","
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.NUMBER_OF_INSTALLMENTS)}
            leftSection={<IconHash stroke={1.5} size={20} />}
            max={100}
            min={2}
            thousandSeparator="."
          />
        ) : (
          <NumberInput
            {...installmentValue.getInputProps()}
            allowDecimal
            allowLeadingZeros={false}
            allowNegative={false}
            decimalSeparator=","
            hideControls
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.AMOUNT_PER_INSTALLMENT)}
            leftSection={`${getCurrencySymbolFromCurrencyCode(company.currency)} `}
            min={1}
            thousandSeparator="."
          />
        )}
        <DateInput
          {...firstDeadlineDate.getInputProps()}
          onChange={date => {
            if (isNullish(date)) return;
            firstDeadlineDate.setValue(new Date(date));
          }}
          label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.FIRST_INSTALLMENT_DATE)}
          leftSection={<IconCalendarWeek stroke={1.5} size={20} />}
          minDate={dayjs().toDate()}
        />
        <Radio.Group
          mt="lg"
          value={delayType.getValue()}
          onChange={val => delayType.setValue(val as DelayType)}
        >
          <Group>
            <Radio
              w="50%"
              label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.DAYS_BETWEEN_INSTALLMENTS)}
              value="days"
            />
            <Radio
              label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.DAY_OF_MONTH_FOR_INSTALLMENTS)}
              value="monthly"
            />
          </Group>
        </Radio.Group>
        {delayType.getValue() === 'days' ? (
          <NumberInput
            {...delayInDays.getInputProps()}
            allowDecimal={false}
            allowNegative={false}
            label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.DAYS_BETWEEN_INSTALLMENTS)}
            leftSection={<IconSunrise stroke={1.5} size={20} />}
            min={1}
            suffix={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.DAYS_SUFFIX)}
          />
        ) : (
          <>
            <DateInput
              label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.DAY_OF_MONTH_FOR_INSTALLMENTS)}
              value={delayDayInMonth.getValue()}
              defaultDate={new Date('01-01-2025')}
              styles={{
                calendarHeader: { display: 'none' },
              }}
              hideOutsideDates
              hideWeekdays
              leftSection={<IconCalendarRepeat stroke={1.5} size={20} />}
              valueFormat="DD"
              weekendDays={[]}
              onChange={val => val && delayDayInMonth.setValue(new Date(val))}
            />
            <Text c="dimmed" size="xs">
              {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.MONTHLY_END_OF_MONTH_DISCLAIMER)}
            </Text>
            <Text c="dimmed" size="xs">
              {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.LAST_DAY_DISCLAIMER)}
            </Text>
          </>
        )}
        <Checkbox
          label={t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.INCLUDE_LATE_FEES)}
          {...lateFees.getInputProps()}
          mt="xs"
        />
        <Button
          disabled={!canClickNext}
          mt="lg"
          onClick={onClickNext}
          rightSection={<IconArrowRight stroke={1.5} size={20} />}
          w="100%"
        >
          {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP3.NEXT)}
        </Button>
      </Stack>
    </Center>
  );
};
